<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import {
  AlertOctagon,
  ArrowRight,
  CalendarCheck2,
  CheckCircle2,
  Clock3,
} from "lucide-vue-next";
import { useWorkspaceStore } from "@/stores/workspace";
import { fullDate, shortDate, todayLocalDate } from "@/utils/dates";
import { calculateTaskProgress } from "@/utils/progress";
import { calculateScheduleHealth } from "@/utils/schedule-health";

const workspace = useWorkspaceStore();
const router = useRouter();
const project = computed(() => workspace.active!.project);
const blockers = computed(() =>
  workspace.active!.tasks.filter(
    (task) => task.type !== "summary" && task.status === "Blocked",
  ),
);
const myTasks = computed(() =>
  workspace
    .active!.tasks.filter(
      (task) =>
        task.ownerId === workspace.user?.id &&
        !["Complete", "Not applicable", "Cancelled"].includes(task.status),
    )
    .sort((left, right) => left.dueDate.localeCompare(right.dueDate))
    .slice(0, 4),
);
const milestones = computed(() =>
  (workspace.active?.tasks || [])
    .filter((task) => task.type === "milestone")
    .sort((left, right) => {
      const leftFinished = ["Complete", "Not applicable", "Cancelled"].includes(
        left.status,
      );
      const rightFinished = [
        "Complete",
        "Not applicable",
        "Cancelled",
      ].includes(right.status);
      if (leftFinished !== rightFinished) return leftFinished ? 1 : -1;
      return (left.dueDate || "9999-12-31").localeCompare(
        right.dueDate || "9999-12-31",
      );
    }),
);
const progress = computed(() =>
  calculateTaskProgress(workspace.active?.tasks || []),
);
const scheduleHealth = computed(() =>
  calculateScheduleHealth({
    tasks: workspace.active?.tasks || [],
    today: todayLocalDate(),
    projectTargetEndDate: project.value.targetEndDate,
  }),
);
function openTask(taskId: string | undefined) {
  if (!taskId) return;
  void router.push({
    path: `/projects/${project.value.id}/list`,
    query: { task: taskId },
  });
}
function sectionName(sectionId: string) {
  return (
    workspace.active?.sections.find((section) => section.id === sectionId)
      ?.name || "Section"
  );
}
function openMilestones() {
  workspace.filters.milestones = true;
  workspace.filters.attention = false;
  void router.push(`/projects/${project.value.id}/list`);
}
function openScheduleAttention() {
  workspace.filters = {
    ...workspace.filters,
    mine: false,
    organization: "All organizations",
    milestones: false,
    attention: true,
    hideInternal: false,
    query: "",
  };
  void router.push({
    path: `/projects/${project.value.id}/list`,
    query: { attention: "1" },
  });
}
</script>

<template>
  <div class="overview">
    <section class="progress-section" aria-labelledby="overview-progress-title">
      <div class="progress-copy">
        <h2 id="overview-progress-title">Progress</h2>
        <strong class="tabular">{{ progress.progress }}% complete</strong>
      </div>
      <div
        class="overview-progress"
        role="progressbar"
        aria-labelledby="overview-progress-title"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="progress.progress"
      >
        <span :style="{ width: `${progress.progress}%` }"></span>
      </div>
    </section>
    <section class="overview-line">
      <div class="milestone-panel">
        <header>
          <div>
            <h2>Milestones</h2>
            <p>Target dates across the Sections visible to you.</p>
          </div>
          <button
            class="button button--secondary"
            type="button"
            @click="openMilestones"
          >
            View in plan <ArrowRight :size="15" />
          </button>
        </header>
        <div v-if="milestones.length" class="milestone-list">
          <button
            v-for="milestone in milestones.slice(0, 5)"
            :key="milestone.id"
            type="button"
            @click="openTask(milestone.id)"
          >
            <span class="overview-icon"><CalendarCheck2 :size="18" /></span
            ><span
              ><strong>{{ milestone.title }}</strong
              ><small
                >{{ sectionName(milestone.sectionId) }} ·
                {{ fullDate(milestone.dueDate) }}</small
              ></span
            ><span
              class="badge"
              :class="
                milestone.status === 'Blocked'
                  ? 'badge--blocked'
                  : milestone.status === 'Complete'
                    ? 'badge--complete'
                    : 'badge--status'
              "
              >{{ milestone.status }}</span
            ><ArrowRight :size="15" />
          </button>
        </div>
        <div v-else class="line-empty">
          <CalendarCheck2 :size="19" />
          <span>No milestones are visible in this project yet.</span>
        </div>
      </div>
      <div class="schedule-focus">
        <span
          class="badge"
          :class="
            scheduleHealth.level === 'On track'
              ? 'badge--complete'
              : scheduleHealth.level === 'Off track'
                ? 'badge--blocked'
                : 'badge--risk'
          "
          >{{ scheduleHealth.level }}</span
        >
        <h3>Schedule health</h3>
        <p>{{ scheduleHealth.reason }}</p>
        <button
          v-if="scheduleHealth.level !== 'On track'"
          class="button button--secondary schedule-attention-action"
          type="button"
          @click="openScheduleAttention"
        >
          View tasks needing attention <ArrowRight :size="15" />
        </button>
        <dl class="schedule-health-signals">
          <div>
            <dt>Overdue</dt>
            <dd>{{ scheduleHealth.overdueCount }}</dd>
          </div>
          <div>
            <dt>Blocked</dt>
            <dd>{{ scheduleHealth.blockedCount }}</dd>
          </div>
          <div>
            <dt>Beyond target</dt>
            <dd>{{ scheduleHealth.beyondTargetCount }}</dd>
          </div>
        </dl>
        <div class="schedule-health-method schedule-health-method--expanded">
          <h4>How health is calculated</h4>
          <ul>
            <li>
              <strong>Off track:</strong> incomplete work is overdue or finishes
              after the project target.
            </li>
            <li>
              <strong>At risk:</strong> no work is off track, but at least one
              incomplete item is blocked.
            </li>
            <li>
              <strong>On track:</strong> no visible incomplete work has any of
              those schedule signals.
            </li>
          </ul>
        </div>
        <details class="schedule-health-method schedule-health-method--mobile">
          <summary>How health is calculated</summary>
          <ul>
            <li>
              <strong>Off track:</strong> incomplete work is overdue or finishes
              after the project target.
            </li>
            <li>
              <strong>At risk:</strong> no work is off track, but at least one
              incomplete item is blocked.
            </li>
            <li>
              <strong>On track:</strong> no visible incomplete work has any of
              those schedule signals.
            </li>
          </ul>
        </details>
      </div>
    </section>
    <div class="overview-columns">
      <section class="decision-list">
        <header>
          <div>
            <h2>Blockers and decisions</h2>
            <p>Items that may change the plan.</p>
          </div>
          <span class="badge badge--blocked">{{ blockers.length }} open</span>
        </header>
        <button
          v-for="task in blockers"
          :key="task.id"
          type="button"
          @click="openTask(task.id)"
        >
          <AlertOctagon :size="18" /><span
            ><strong>{{ task.title }}</strong
            ><small>{{ task.blocker }} · {{ task.ownerName }}</small></span
          ><ArrowRight :size="16" />
        </button>
        <div v-if="!blockers.length" class="line-empty">
          <CheckCircle2 :size="19" /><span>No open blockers in this view.</span>
        </div>
      </section>
      <section class="next-actions">
        <header>
          <div>
            <h2>My next actions</h2>
            <p>Owned work ordered by due date.</p>
          </div>
        </header>
        <button
          v-for="task in myTasks"
          :key="task.id"
          type="button"
          @click="openTask(task.id)"
        >
          <span
            class="action-status"
            :class="task.health === 'At risk' ? 'risk' : ''"
            ><Clock3 :size="16" /></span
          ><span
            ><strong>{{ task.title }}</strong
            ><small
              >{{ task.status }} · Due {{ shortDate(task.dueDate) }}</small
            ></span
          ><ArrowRight :size="16" />
        </button>
        <div v-if="!myTasks.length" class="line-empty">
          <CheckCircle2 :size="19" /><span>No owned work needs attention.</span>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.overview {
  height: 100%;
  overflow: auto;
  display: grid;
  align-content: start;
  gap: 18px;
  padding: 24px 28px 50px;
  background: var(--paper);
}
.overview > section,
.overview-columns > section {
  border: 1px solid var(--rule);
  background: white;
}
.overview-line {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
}
.milestone-panel {
  border-right: 1px solid var(--rule);
}
.milestone-panel > header {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--rule);
}
.milestone-panel > header p {
  color: var(--text-soft);
}
.milestone-list {
  display: grid;
}
.milestone-list > button {
  min-height: 58px;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto 18px;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  border-bottom: 1px solid var(--rule);
  background: white;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.milestone-list > button:last-child {
  border-bottom: 0;
}
.milestone-list > button:hover,
.milestone-list > button:focus-visible {
  background: var(--sheet-subtle);
}
.milestone-list > button > span:nth-child(2) {
  min-width: 0;
  display: grid;
}
.milestone-list strong,
.milestone-list small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.milestone-list small {
  color: var(--text-soft);
}
.overview-icon {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  color: var(--shared);
  background: var(--shared-soft);
}
.schedule-focus {
  display: grid;
  align-content: center;
  gap: 4px;
  padding: 20px 22px;
}
.schedule-focus p {
  color: var(--text-soft);
}
.schedule-health-signals {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}
.schedule-health-signals div {
  min-width: 0;
  padding-top: 8px;
  border-top: 1px solid var(--rule);
}
.schedule-health-signals dt {
  color: var(--text-faint);
  font-size: 11px;
}
.schedule-health-signals dd {
  margin-top: 2px;
  color: var(--text);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.schedule-health-method {
  margin-top: 10px;
  color: var(--text-soft);
  font-size: 12px;
}
.schedule-health-method h4,
.schedule-health-method summary {
  width: fit-content;
  color: var(--shared);
  font-size: 12px;
  font-weight: 700;
}
.schedule-health-method summary {
  cursor: pointer;
}
.schedule-health-method--mobile {
  display: none;
}
.schedule-health-method ul {
  display: grid;
  gap: 5px;
  margin: 9px 0 0;
  padding-left: 18px;
}
.schedule-health-method strong {
  color: var(--text);
}
.progress-section {
  padding: 20px 22px;
}
.progress-copy {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 20px;
}
.progress-copy strong {
  color: var(--text-soft);
  font-size: 14px;
}
.overview-progress {
  position: relative;
  height: 12px;
  margin-top: 18px;
  overflow: visible;
  border-radius: 2px;
  background: #dfe6e4;
}
.overview-progress > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--brand);
}
.overview-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}
.decision-list header,
.next-actions header {
  min-height: 72px;
  display: flex;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid var(--rule);
}
.decision-list header p,
.next-actions header p {
  color: var(--text-soft);
}
.decision-list > button,
.next-actions > button {
  width: 100%;
  min-height: 62px;
  display: grid;
  grid-template-columns: 25px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--rule);
  background: white;
  color: var(--danger);
  text-align: left;
  cursor: pointer;
}
.decision-list > button:hover,
.next-actions > button:hover {
  background: var(--sheet-subtle);
}
.decision-list > button:disabled,
.next-actions > button:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}
.decision-list > button:disabled:hover,
.next-actions > button:disabled:hover {
  background: white;
}
.decision-list > button span,
.next-actions > button span {
  display: grid;
  color: var(--text);
}
.decision-list small,
.next-actions small {
  color: var(--text-soft);
}
.next-actions > button {
  color: var(--shared);
}
.action-status {
  width: 26px;
  height: 26px;
  display: grid !important;
  place-items: center;
  border-radius: 50%;
  color: var(--shared) !important;
  background: var(--shared-soft);
}
.action-status.risk {
  color: var(--warning) !important;
  background: var(--warning-soft);
}
.line-empty {
  min-height: 90px;
  display: flex;
  place-content: center;
  align-items: center;
  gap: 8px;
  color: var(--success);
}
@media (max-width: 850px) {
  .overview {
    padding: 16px;
  }
  .overview-line,
  .overview-columns {
    grid-template-columns: 1fr;
  }
  .milestone-panel {
    border-right: 0;
    border-bottom: 1px solid var(--rule);
  }
  .milestone-panel > header {
    align-items: flex-start;
    flex-direction: column;
  }
  .milestone-list > button {
    grid-template-columns: 34px minmax(0, 1fr) auto;
  }
  .milestone-list > button > svg {
    display: none;
  }
}
@media (max-width: 600px) {
  .schedule-health-method--expanded {
    display: none;
  }
  .schedule-health-method--mobile {
    display: block;
  }
}
</style>
